import { FC, useEffect } from 'react';
import { IonCard } from '@ionic/react';
import { useHistory, useParams } from 'react-router';
import Reference from './components/Reference';
import BreadCrumbs from '../../components/Manager/BreadCrumbs/BreadCrumbs';
import ComponentSectionContextProvider from '../../context/ComponentSectionContext';
import useRoutes from '../../hooks/useRoutes';
import { useTranslate } from '../../hooks/context/useTranslate';
import { useLayoutEditorContext } from '../../hooks/context/useLayoutEditorContext';
import { useLayoutHandlersContext } from '../../hooks/context/useLayoutHandlersContext';
import { BreadCrumbCrumb } from '../../types/types';

// Fullscreen reference picker of the layout editor. A regular route (like the
// form references), but without the token chain — the picker state lives in
// LayoutEditorContext, which stays mounted (the editor route matches this URL too).
const LayoutEditorReference: FC = () => {
  const history = useHistory();
  const routes = useRoutes();
  const { datasetId, layoutId, objectId } = useParams<{
    datasetId: string;
    layoutId: string;
    objectId: string;
  }>();
  const { t } = useTranslate();
  const {
    layout,
    fullscreenReference,
    closeFullscreenReference,
    submitFullscreenReference,
  } = useLayoutEditorContext();
  const { fireFieldEvent } = useLayoutHandlersContext();

  const editorUrl = routes.layoutEditor(datasetId, layoutId, objectId);

  // Editor → reference trail; the editor crumb links back (the stale picker
  // state left behind is cleared by LayoutEditorContent on mount). A reference
  // opened from the "generate matrix" dialog shows the dialog as the previous
  // step — the link reopens it (its state lives in LayoutEditorContext).
  const isFromMatrixDialog = fullscreenReference?.scope === 'matrix';
  const breadcrumbs: BreadCrumbCrumb[] = [
    {
      url: editorUrl,
      name: isFromMatrixDialog
        ? t('layoutEditor.generateMatrix')
        : (layout?.name ?? t('layoutEditor.title')),
      tokenId: isFromMatrixDialog ? 'layout-editor-matrix' : 'layout-editor',
      isActive: false,
    },
    {
      url: '',
      name: fullscreenReference?.reference.label ?? '',
      tokenId: 'layout-editor-reference',
      isActive: true,
    },
  ];

  const handleClose = (): void => {
    closeFullscreenReference();
    history.replace(editorUrl);
  };

  const handleSubmit = (value: string): void => {
    submitFullscreenReference(value);

    // The confirmed value fires the field's handlerTriggers — change → touch,
    // the same order an autocomplete selection produces.
    if (fullscreenReference) {
      const { scope, reference } = fullscreenReference;
      fireFieldEvent(scope, reference.name, 'change', value);
      fireFieldEvent(scope, reference.name, 'touch', value);
    }

    history.replace(editorUrl);
  };

  // Direct URL / refresh: there is no picker state to show — back to the editor.
  useEffect(() => {
    if (!fullscreenReference) {
      history.replace(editorUrl);
    }
  }, [fullscreenReference]);

  if (!fullscreenReference) {
    return null;
  }

  return (
    <div className="page-container">
      <IonCard>
        <ComponentSectionContextProvider componentSection="form-reference">
          <BreadCrumbs breadcrumbs={breadcrumbs} />
          <Reference
            fullscreenReference={fullscreenReference.reference}
            handleCloseFullscreenReference={handleClose}
            handleOnSubmit={handleSubmit}
          />
        </ComponentSectionContextProvider>
      </IonCard>
    </div>
  );
};

export default LayoutEditorReference;
